import { useEffect, useState } from "react";

const STORAGE_KEY_PREFIX = "table_columns_";

type ColumnVisibilityMap = Record<string, boolean>;

function getStoredVisibleColumns(
  tableKey: string,
  allColumns: string[],
): string[] {
  const storageKey = STORAGE_KEY_PREFIX + tableKey;
  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    const initial: ColumnVisibilityMap = Object.fromEntries(
      allColumns.map((col) => [col, false]),
    );
    localStorage.setItem(storageKey, JSON.stringify(initial));
    return allColumns;
  }

  try {
    const parsed = JSON.parse(raw) as ColumnVisibilityMap;

    const merged: ColumnVisibilityMap = {};
    for (const col of allColumns) {
      merged[col] = parsed[col] ?? false;
    }

    localStorage.setItem(storageKey, JSON.stringify(merged));

    return allColumns.filter((col) => !merged[col]);
  } catch {
    const fallback: ColumnVisibilityMap = Object.fromEntries(
      allColumns.map((col) => [col, false]),
    );
    localStorage.setItem(storageKey, JSON.stringify(fallback));
    return allColumns;
  }
}

function setStoredVisibleColumns(
  tableKey: string,
  allColumns: string[],
  visibleColumns: string[],
) {
  const storageKey = STORAGE_KEY_PREFIX + tableKey;

  const visibility: ColumnVisibilityMap = Object.fromEntries(
    allColumns.map((col) => [col, !visibleColumns.includes(col)]),
  );

  localStorage.setItem(storageKey, JSON.stringify(visibility));
}

export function useVisibleColumns(tableKey: string, allColumns: string[]) {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    getStoredVisibleColumns(tableKey, allColumns),
  );

  useEffect(() => {
    setStoredVisibleColumns(tableKey, allColumns, visibleColumns);
  }, [tableKey, visibleColumns, allColumns]);

  useEffect(() => {
    const next = getStoredVisibleColumns(tableKey, allColumns);
    if (JSON.stringify(next) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(next);
    }
  }, [tableKey, allColumns.join(",")]);

  return [visibleColumns, setVisibleColumns] as const;
}
